import { REVIEW_MODE_CUSTOM } from '../../../components/client-form/review-seed-presets';
import Utils from '../../../utils/utils';
import { resolveExpenseType } from './contract-expense-json';
import { isBlankGuestCount } from './guest-count-validity';

// Layer 2: static mapping from an XPENS category name to the extra field it
// triggers on the review form. XPEN-3191 / XPEN-3183-008 (D6): re-keyed from
// the retired 26-category list to the sixteen. Mileage is deliberately absent
// — the Cost Type already owns it via triggers_extra_field, and mileage
// expenses come through their own dedicated flow (json.type === 'mileage'),
// never by picking a category. Only Breakfast/Lunch/Dinner trigger Guests —
// Coffee & Snacks and Meals – Unspecified are meal-adjacent but were never
// part of the approved list (D6); Entertainment & Client Hospitality is
// deliberately excluded too (confirmed twice by management). Lodging is
// included in Location so clients whose old "Travel" category links to
// "Lodging" don't lose the field. Parking (XPEN-3435) shows Location too, as
// it did when parking receipts were filed under Ground Transportation.
//
// Keys keep their inner spaces (display-name format) — resolveAiExtraField
// lower-cases, trims, AND turns hyphens into spaces before looking up, so a
// stable category `code` (hyphenated, e.g. "air-travel" — what
// imputation-config-modal.js's linkage picker has saved into
// maps_from_verify_category since XPEN-3187) matches the same entry as its
// display name ("Air Travel"). Single-word categories (lodging, breakfast...)
// happen to be code-and-name-identical once lower-cased, which is what let
// this gap go unnoticed until a two-word category was added here. This is
// the opposite convention from expense-category-icon.js's normalizer (which
// strips ALL punctuation, not just hyphens) — do not copy keys between them.
const CATEGORY_EXTRA_FIELD_MAP = {
  'breakfast': 'guests',
  'lunch': 'guests',
  'dinner': 'guests',
  'air travel': 'location',
  'ground transportation': 'location',
  'parking': 'location',
  'lodging': 'location',
};

function resolveAiExtraField(aiCategory) {
  if (!aiCategory || typeof aiCategory !== 'string') return null;
  const key = aiCategory.toLowerCase().trim().replace(/-/g, ' ');
  // hasOwnProperty guard: a plain object literal inherits Object.prototype,
  // so a category value like "__proto__" or "constructor" would otherwise
  // resolve to an inherited object instead of undefined. Every caller here
  // does a strict === 'guests' / 'location' comparison, so this was never
  // exploitable, but the guard closes it outright rather than relying on
  // every future caller getting that comparison right.
  if (!Object.prototype.hasOwnProperty.call(CATEGORY_EXTRA_FIELD_MAP, key)) return null;
  return CATEGORY_EXTRA_FIELD_MAP[key];
}

/** Veryfi canonical category from an imputation row (maps_from_verify_category wins). */
export function resolveVerifyCategoryFromImputationRow(row) {
  if (!row || typeof row !== 'object') return null;
  const raw = row.maps_from_verify_category ?? row.category;
  if (raw == null || String(raw).trim() === '') return null;
  return String(raw).trim();
}

/**
 * Activity is visible when the expense is billable.
 * For Elite clients, the matter must also have pta_group_cost set (Elite-specific matter flag).
 * For all other adapters, billable alone is sufficient.
 */
export const isActivityVisible = (matter, isBillable, isElite = false) => {
  if (!isBillable) return false;
  if (isElite) return !!matter?.value?.pta_group_cost;
  return true;
};

/**
 * Layer 1: triggers_extra_field on CostType (admin-set, wins when present).
 * Layer 2: AI-extracted category fallback (when triggers_extra_field is null/absent).
 * Layer 2b: selected imputation row (maps_from_verify_category / category) — same Veryfi
 *     keys as prefill (XPEN-2520), so manual Category selection matches AI pre-fill.
 * Layer 3 (Elite only): keyword fallback — "travel" description, excluding GL code 564K.
 */
export const isLocationVisible = (
  costType,
  aiCategory,
  isElite = false,
  imputationVerifyCategory = null,
) => {
  const triggerField = costType?.value?.triggers_extra_field ?? null;
  if (triggerField != null) return triggerField === 'location';
  if (resolveAiExtraField(aiCategory) === 'location') return true;
  if (resolveAiExtraField(imputationVerifyCategory) === 'location') return true;
  if (isElite) {
    return (
      costType?.value?.description?.toLowerCase()?.includes('travel') &&
      costType?.value?.cost_type !== '564K'
    );
  }
  return false;
};

/**
 * XPEN-3268: guests are category-only. Cost Type `triggers_extra_field` is ignored
 * (callers may still pass `costType`). Breakfast / Lunch / Dinner from the AI
 * (`aiCategory`) or from the selected imputation row still show the box.
 * json.Category is never cleared when a reviewer corrects the category via the
 * imputation picker — only imputationVerifyCategory changes — so a non-guests
 * AI category must still fall through to the link check (D6b).
 */
export const isNumberOfGuestsVisible = (costType, aiCategory, imputationVerifyCategory = null) => {
  if (resolveAiExtraField(aiCategory) === 'guests') return true;
  return resolveAiExtraField(imputationVerifyCategory) === 'guests';
};

/**
 * Whether the Number of Guests box should render.
 * Preset modes (Client-only, Client / Non-client) follow category only.
 * Custom also requires `field_number_of_guests` (opt-in, default off).
 */
export const shouldShowNumberOfGuests = (data, costType, aiCategory, imputationVerifyCategory = null) => {
  if (!isNumberOfGuestsVisible(costType, aiCategory, imputationVerifyCategory)) {
    return false;
  }
  const mode = data?.config?.expenses_review?.mode;
  if (mode === REVIEW_MODE_CUSTOM) {
    return Utils.hasExpenseField(data, 'field_number_of_guests', false);
  }
  return true;
};

/**
 * Whether an empty guest count blocks save. Required only in Custom when
 * `field_number_of_guests` is on and listed in `required_fields`.
 */
export const isNumberOfGuestsRequired = (data, costType, aiCategory, imputationVerifyCategory = null) => {
  if (!shouldShowNumberOfGuests(data, costType, aiCategory, imputationVerifyCategory)) {
    return false;
  }
  const mode = data?.config?.expenses_review?.mode;
  if (mode !== REVIEW_MODE_CUSTOM) {
    return false;
  }
  return Utils.isExpenseFieldRequired(data, 'field_number_of_guests');
};

export const isMileageVisible = (costType, aiCategory) => {
  const triggerField = costType?.value?.triggers_extra_field ?? null;
  if (triggerField != null) return triggerField === 'mileage';
  const aiField = resolveAiExtraField(aiCategory);
  if (aiField != null) return aiField === 'mileage';
  return false;
};

/**
 * Clean expense by resetting hidden conditional fields to empty string
 * @param {Object} expense - The expense object to clean
 * @param {Object} [data] - Client store (MobX `data`); when set, clears values for expense `field_*` flags that are off so saves do not leak stale UI state.
 * @returns {Object} - A new expense object with hidden fields reset
 */
export const cleanConditionalFields = (expense, data) => {
  const cleanedExpense = { ...expense, info: { ...(expense.info || {}) } };

  // Snapshot the AI-extracted category before cleanup so the Layer-2 extra-field
  // visibility checks below still fire correctly. The legacy picker write path
  // (cleanedExpense.category) is cleared unconditionally to prevent stale form
  // state from leaking into the payload.
  const aiCategory = expense.category;

  if (data != null) {
    if (!Utils.hasExpenseField(data, 'field_matter')) {
      cleanedExpense.matter = '';
      cleanedExpense.matterSplits = null;
    }
    if (!Utils.hasExpenseField(data, 'field_matter_split', false)) {
      cleanedExpense.matterSplits = null;
    }
    if (!Utils.hasExpenseField(data, 'field_expense_date')) {
      cleanedExpense.date = '';
    }
    cleanedExpense.category = '';
    if (!Utils.hasExpenseField(data, 'field_cost_type')) {
      cleanedExpense.costType = '';
    }
    if (!Utils.hasExpenseField(data, 'field_activity')) {
      cleanedExpense.activity = null;
    }
    if (!Utils.hasExpenseField(data, 'field_narrative')) {
      cleanedExpense.info.narrative = '';
    }
    if (!Utils.hasExpenseField(data, 'field_tips')) {
      cleanedExpense.info.tip = null;
    }
    if (!Utils.hasExpenseField(data, 'field_tax_code')) {
      cleanedExpense.info.taxCode = null;
    }
    if (!Utils.hasExpenseField(data, 'field_imputation_level1', false)) {
      cleanedExpense.imputationLevel1Id = null;
      cleanedExpense.imputationLeafId = null;
    }
    // XPEN-3653 (XPEN-3733): Expense Type is not reset when its field is hidden.
    // The category sets it on the form, it is saved, and the Non-client cleanup
    // just below runs against that same type, so a hidden Matter is not saved.
    // XPEN-3653-006: with the Billable field hidden, a Billable the form set together
    // with a Client type (isBillableSetByType) is kept here (the Non-client cleanup just
    // below still removes it); any other is removed so the stored one is kept (it may
    // have been changed in the expense editor).
    if (!Utils.hasExpenseField(data, 'field_billable', false) && !cleanedExpense.isBillableSetByType) {
      delete cleanedExpense.isBillable;
    }
    if (cleanedExpense.expenseType === 'internal') {
      cleanedExpense.matter = '';
      cleanedExpense.matterSplits = null;
      delete cleanedExpense.isBillable;
    }
    if (cleanedExpense.isBillable === false) {
      cleanedExpense.matterSplits = null;
    }
  }

  const isElite = Utils.isExportFormat(data, 'elite');
  const cleanedExpenseInfo = { ...cleanedExpense.info };

  // Reset activity if matter doesn't require it
  const hasActivity = cleanedExpense.activity &&
                      cleanedExpense.activity !== '' &&
                      (typeof cleanedExpense.activity !== 'object' || cleanedExpense.activity.value || cleanedExpense.activity.activity_id || Object.keys(cleanedExpense.activity).length > 0);
  if (!isActivityVisible(cleanedExpense.matter, cleanedExpense.isBillable !== false, isElite) && hasActivity) {
    cleanedExpense.activity = '';
  }

  if (
    (!Utils.hasExpenseField(data, 'field_location', false)
      || !isLocationVisible(
        cleanedExpense.costType,
        aiCategory,
        isElite,
        expense.imputationVerifyCategory,
      ))
    && cleanedExpense.info?.location
  ) {
    cleanedExpenseInfo.location = '';
  }

  if (
    !shouldShowNumberOfGuests(data, cleanedExpense.costType, aiCategory, expense.imputationVerifyCategory)
    && !isBlankGuestCount(cleanedExpense.info?.numberOfGuests)
  ) {
    cleanedExpenseInfo.numberOfGuests = '';
  }

  if (!isMileageVisible(cleanedExpense.costType, aiCategory) && cleanedExpense.info?.distance) {
    cleanedExpenseInfo.distance = '';
  }

  cleanedExpense.info = cleanedExpenseInfo;
  return cleanedExpense;
};

/**
 * XPEN-2508 / XPEN-2509 — Strip dual-travel mileage keys from persisted expense `json`
 * when the review UI would not collect them, so PUT payloads do not leak stale state.
 * Called from `buildExpensePayload` (same module consumer as `cleanConditionalFields`).
 *
 * @param {Object} json - expenseData.json (spread clone recommended)
 * @param {Object} data - MobX client store
 * @param {*} costType - review form cost type (react-select shape or legacy)
 * @param {string} [aiCategory] - AI / Veryfi category string for Layer-2 visibility
 * @returns {Object} new json object
 */
export function stripDualTravelMileageJsonFields(json, data, costType, aiCategory) {
  if (!json || typeof json !== 'object') {
    return json;
  }
  const out = { ...json };
  const isMileageDoc = out.type === 'mileage';
  if (!isMileageDoc) {
    delete out.travel_type;
    delete out.departure_office_id;
    delete out.destination_office_id;
    return out;
  }
  const mileageUiVisible = isMileageVisible(costType, aiCategory);
  if (!mileageUiVisible) {
    delete out.travel_type;
    delete out.departure_office_id;
    delete out.destination_office_id;
    out.AddressOrigin = '';
    out.AddressDestination = '';
    return out;
  }
  const tt = out.travel_type ?? 'inter_office';
  if (tt !== 'inter_office') {
    delete out.departure_office_id;
    delete out.destination_office_id;
  }
  return out;
}

/**
 * XPEN-2807 — apply review ExpenseType side effects to expense editor `json`.
 * Mirrors review form onChange + cleanConditionalFields internal branch.
 *
 * @param {Object} json - expense editor payload (`ExpenseType`, `IsBillable`, `MatterId`, …)
 * @param {string} expenseType - `'client'`, `'internal'`, or `''` when cleared
 * @returns {Object} updated json (shallow clone)
 */
export function applyExpenseTypeToExpenseEditorJson(json, expenseType) {
  const next = { ...(json || {}), ExpenseType: expenseType };
  if (expenseType === 'client') {
    next.IsBillable = true;
  } else if (expenseType === 'internal') {
    next.IsBillable = false;
    next.MatterId = '';
    if (next.MatterSplits != null) {
      next.MatterSplits = null;
    }
  }
  return next;
}

/**
 * XPEN-2807 — apply ExpenseType side effects to a single expense line item.
 *
 * @param {Object} item - ListItem row
 * @param {string} expenseType - `'client'`, `'internal'`, or `''`
 * @returns {Object} updated item (shallow clone)
 */
export function applyExpenseTypeToListItem(item, expenseType) {
  const next = { ...(item || {}) };
  if (expenseType === 'client') {
    next.IsBillable = 1;
  } else if (expenseType === 'internal') {
    next.IsBillable = 0;
    next.MatterId = '';
  }
  return next;
}

/** Mirrors xpens-api `expense_is_non_client_billable` — internal scope must not be billable. */
export function isExpenseJsonBillableValue(value) {
  return value === true || value === 1;
}

/** @returns {boolean} True when ExpenseType resolves to internal and the scope is billable. */
export function expenseIsNonClientBillable(json = {}, item = null) {
  if (!json || typeof json !== 'object') {
    return false;
  }
  if (resolveExpenseType(json, item) !== 'internal') {
    return false;
  }
  if (item != null) {
    if (isExpenseJsonBillableValue(item.IsBillable)) {
      return true;
    }
    if (!Object.prototype.hasOwnProperty.call(item, 'IsBillable')) {
      return isExpenseJsonBillableValue(json.IsBillable);
    }
    return false;
  }
  return isExpenseJsonBillableValue(json.IsBillable);
}

/** Scan expense header and lines for the prohibited internal + billable combo. */
export function expenseJsonHasNonClientBillable(json = {}) {
  if (!json || typeof json !== 'object') {
    return false;
  }
  if (expenseIsNonClientBillable(json, null)) {
    return true;
  }
  return (json.ListItem ?? []).some(
    (item) => item && typeof item === 'object' && expenseIsNonClientBillable(json, item),
  );
}

/** Force non-billable on every internal scope that is still billable. */
export function sanitizeNonClientBillableJson(json = {}) {
  if (!json || typeof json !== 'object') {
    return json;
  }
  const next = { ...json };
  if (expenseIsNonClientBillable(next, null)) {
    next.IsBillable = false;
  }
  if (Array.isArray(next.ListItem)) {
    next.ListItem = next.ListItem.map((item) => {
      if (!item || typeof item !== 'object' || !expenseIsNonClientBillable(next, item)) {
        return item;
      }
      return { ...item, IsBillable: 0 };
    });
  }
  return next;
}

